import metaEn from "./metaEn.json";
import metaEs from "./metaEs.json";

export const strings = {
  en: {
    nav: {
      products: "Products",
      standard: "The standard",
      professionals: "Professionals",
      enterprise: "Enterprise",
      press: "Press",
      menu: "Menu",
      close: "Close",
    },
    hero: {
      eyebrow: "Independent software studio — building since 2010",
      lines: ["Software that earns", "its place in", "clinical work."],
      sub: "VLR Software builds privacy-first reference tools for clinicians, coders, and careful teams. Offline-first. No accounts. No tracking.",
      cta1: "Explore products",
      cta2: "For clinicians & partners",
      scroll: "Scroll",
      deviceCaption: ["ICD-10 On the Go", "ICD-10-CM 2026", "Acroly"],
    },
    trust: [
      "Building since 2010",
      "Offline-first, by design",
      "No accounts. No tracking.",
      "Free & premium tiers",
    ],
    marquee: [
      "Offline-first",
      "No accounts",
      "No tracking",
      "iCloud sync",
      "Updated for 2026",
      "Building since 2010",
      "iPhone · iPad · Mac",
    ],
    products: {
      eyebrow: "Portfolio",
      title: "Professional & Medical Apps",
      gamesTitle: "Games",
      available: "Available now",
      updated: "Recently updated",
      comingSoon: "Coming soon",
      flagship: "Flagship",
      downloadOn: "Download on the",
      store: "App Store",
    },
    dev: {
      eyebrow: "In development",
      title: "What we're building next",
      note: "Early access for partners — get in touch.",
      status: "Coming soon",
    },
    standard: {
      eyebrow: "The VLR standard",
      title: "Three rules we don't break.",
      chapters: [
        {
          n: "01",
          t: "Clarity before novelty",
          d: "Every screen answers one question at a time. If a feature doesn't make the answer faster or clearer, it doesn't ship.",
        },
        {
          n: "02",
          t: "Privacy from the beginning",
          d: "Our apps work offline, keep your data on your devices, and never phone home. There is nothing to breach because there is nothing to take.",
        },
        {
          n: "03",
          t: "Built for continued use",
          d: "We maintain what we ship — yearly code-set updates, new OS support, and fixes that arrive before you notice the problem.",
        },
      ],
    },
    pro: {
      eyebrow: "For professionals",
      title: "Licensing and custom software for teams.",
      body: "Volume purchases through Apple Business Manager, plus custom development when off-the-shelf doesn't fit. We build software — nothing else.",
      items: ["Bulk licensing", "Custom builds", "In-development pilots"],
      cta: "Start a conversation",
    },
    studio: {
      eyebrow: "A note from the studio",
      body: "VLR Software is a small, independent studio. We build the tools we want to exist — quiet, fast, and respectful of your data. No growth loops, no engagement tricks. Software that does its job, then gets out of the way. If that sounds old-fashioned, we're fine with it.",
      sig: "VLR Software LLC",
    },
    productPage: {
      back: "All products",
      inHand: "In the hand",
      whatsNew: "What's new",
      about: "About the product",
      documents: "Product documentation",
      keyFeatures: "Key features",
      version: "Version",
      updated: "Updated",
      pricing: "Pricing",
      privacySummary: "Privacy summary",
      privacyTitle: "Nothing to breach, because there's nothing to take.",
      privacyMore: "Full details in our",
      privacyPolicy: "privacy policy",
      nextProduct: "Next product",
    },
    enterprise: {
      eyebrow: "Enterprise & partnerships",
      title: "Software, built for your team.",
      body: "For clinicians, coders, and partners who need VLR Software tools at team scale — or something built specifically for them.",
      offerings: [
        { n: "01", title: "Volume licensing", body: "Purchase any VLR Software app in quantity through Apple Business Manager and distribute managed licenses across your organization." },
        { n: "02", title: "In-development pilots", body: "Early access and a real-world feedback loop for apps in development, shaped with the teams who will use them." },
        { n: "03", title: "Custom development", body: "Purpose-built tools on the same standard — offline-first, privacy-first, maintained. Scoped quietly, delivered carefully." },
      ],
      scaleNote: "Offline-first · No accounts · No tracking — at any scale",
      formTitle: "Start a conversation",
      formNote: "We reply within two business days. Nothing you send is stored on this site.",
      labels: { name: "Name", organization: "Organization", email: "Email", interest: "I'm interested in", message: "Message" },
      placeholders: { name: "Your name", organization: "Clinic, hospital, or company", email: "you@organization.com", message: "Tell us about your team and what you need." },
      options: ["Volume licensing", "In-development pilots", "Custom development", "Early access to apps in development", "Something else"],
      submit: "Send enquiry",
      confirmation: "Your email client should have opened with the enquiry pre-filled — just press send.",
    },
    press: {
      eyebrow: "Press & media",
      title: "The facts, ready to print.",
      body: "Everything a newsroom needs to write about VLR Software — no embargo, no spin. For interviews or review copies, write to us directly.",
      boilerplateLabel: "Boilerplate",
      boilerplate: "VLR Software LLC is an independent software studio registered in the United States. Since 2011 it has built focused, privacy-first tools for health, finance, and professional work — including the ICD-10 reference family used by clinicians and medical coders, and Acroly, a medical abbreviation library for iPhone, iPad, and Mac. VLR Software apps work offline, hold no accounts, and run no tracking.",
      productsLabel: "Products",
      factsLabel: "Key facts",
      facts: [["Company", "VLR Software LLC"], ["Type", "Independent, privately held"], ["Registered", "United States"], ["Shipping on the App Store", "Since 2011"], ["Focus", "Health, finance, and professional work"], ["Principles", "Clarity · Privacy · Continued use"]],
      kitLabel: "Brand asset",
      kitText: "Download the approved VLR Software logo for editorial use. For interviews, review materials, or additional assets, contact us directly.",
      kitCta: "Download logo (SVG)",
      contactCta: "Contact for interviews",
    },
    notFound: {
      eyebrow: "Error 404",
      title: "This page took a wrong turn.",
      body: "The link you followed doesn't exist — but everything we make is right here.",
      cta: "Back to homepage",
      productsCta: "Explore products",
    },
    meta: metaEn,
    legalPage: {
      privacy: "Privacy Policy",
      terms: "Terms of Use",
    },
    legal: {
      about: {
        eyebrow: "Company",
        title: "Professional software, built with purpose.",
        updated: null,
        paragraphs: [
          "VLR Software LLC creates focused mobile products for people who depend on clear information, private workflows, and dependable tools.",
          "Building since 2010 — More than a decade of product work across medical reference, personal finance, and professional utilities, supported through careful updates and direct customer service.",
          "Privacy as architecture — We favor on-device processing, user-controlled storage, and clear boundaries. Sensitive data should not travel farther than the task requires.",
          "One standard across every product — Different apps share the same foundation: useful hierarchy, transparent behavior, accessible interaction, and attention to long-term quality.",
        ],
        factsTitle: "Company information",
        facts: [
          ["Legal name", "VLR Software LLC"],
          ["Mailing address", "30 N Gould St Ste R\nSheridan, WY 82801\nUnited States"],
          ["Business", "Independent mobile software studio"],
          ["Official contact", "support@vlrsoftware.com"],
        ],
        quote: "The goal is not to add more software to your day. It is to make an important part of your day clearer.",
      },
      privacy: {
        eyebrow: "Legal",
        title: "Privacy is a product decision.",
        updated: "Last updated July 2026",
        paragraphs: [
          "This page explains the company-wide approach. Product-specific details are published on every app's own privacy page.",
          "Our approach — VLR Software LLC designs its applications to avoid unnecessary accounts, tracking, and server-side collection. We seek to collect only what a feature genuinely needs.",
          "Product-specific details — Different apps may use different platform services such as iCloud, StoreKit, notifications, or on-device intelligence. Each product publishes its own privacy policy so those differences remain explicit.",
          "Platform services — When a feature uses services provided by Apple, Google, or another platform provider, that service is also governed by the provider's privacy policy and terms.",
          "Questions — If you have a privacy question about the site or any VLR Software LLC product, contact support@vlrsoftware.com.",
        ],
      },
      terms: {
        eyebrow: "Legal",
        title: "Terms & Conditions",
        updated: null,
        markdown: true,
        paragraphs: [],
      },
    },
    footer: {
      products: "Products",
      company: "Company",
      legal: "Legal",
      contact: "Contact",
      emailUs: "Email us",
      comingSoon: "Coming soon",
      flagship: "Flagship",
      about: "About",
      press: "Press",
      enterprise: "Enterprise",
      privacy: "Privacy",
      terms: "Terms",
      language: "Language",
      rights: "© 2026 VLR Software LLC. All rights reserved.",
      tagline: "Privacy-first software for health, finance, and professional work.",
    },
  },
  es: {
    nav: {
      products: "Productos",
      standard: "El estándar",
      professionals: "Profesionales",
      enterprise: "Empresas",
      press: "Prensa",
      menu: "Menú",
      close: "Cerrar",
    },
    hero: {
      eyebrow: "Estudio de software independiente — desde 2010",
      lines: ["Software que se gana", "su lugar en el", "trabajo clínico."],
      sub: "VLR Software crea herramientas de referencia que priorizan la privacidad para médicos, codificadores y equipos exigentes. Sin conexión. Sin cuentas. Sin rastreo.",
      cta1: "Explorar productos",
      cta2: "Para médicos y socios",
      scroll: "Desplázate",
      deviceCaption: ["ICD-10 On the Go", "ICD-10-CM 2026", "Acroly"],
    },
    trust: [
      "Construyendo desde 2010",
      "Diseño offline, desde el inicio",
      "Sin cuentas. Sin rastreo.",
      "Niveles gratis y premium",
    ],
    marquee: [
      "Offline-first",
      "Sin cuentas",
      "Sin rastreo",
      "Sincronización iCloud",
      "Actualizado para 2026",
      "Desde 2010",
      "iPhone · iPad · Mac",
    ],
    products: {
      eyebrow: "Portafolio",
      title: "Apps profesionales y médicas",
      gamesTitle: "Juegos",
      available: "Disponible ahora",
      updated: "Actualizada recientemente",
      comingSoon: "Próximamente",
      flagship: "Insignia",
      downloadOn: "Descargar en el",
      store: "App Store",
    },
    dev: {
      eyebrow: "En desarrollo",
      title: "Lo que estamos construyendo",
      note: "Acceso anticipado para socios — escríbenos.",
      status: "Próximamente",
    },
    standard: {
      eyebrow: "El estándar VLR",
      title: "Tres reglas que no rompemos.",
      chapters: [
        {
          n: "01",
          t: "Claridad antes que novedad",
          d: "Cada pantalla responde una pregunta a la vez. Si una función no hace la respuesta más rápida o más clara, no se publica.",
        },
        {
          n: "02",
          t: "Privacidad desde el principio",
          d: "Nuestras apps funcionan sin conexión, guardan tus datos en tus dispositivos y nunca se comunican con servidores. No hay nada que vulnerar porque no hay nada que robar.",
        },
        {
          n: "03",
          t: "Hecho para perdurar",
          d: "Mantenemos lo que publicamos — actualizaciones anuales de códigos, compatibilidad con nuevos sistemas y correcciones que llegan antes de que notes el problema.",
        },
      ],
    },
    pro: {
      eyebrow: "Para profesionales",
      title: "Licencias y software a medida para equipos.",
      body: "Compras por volumen a través de Apple Business Manager y desarrollo personalizado cuando lo estándar no es suficiente. Construimos software — nada más.",
      items: ["Licencias por volumen", "Desarrollo personalizado", "Pilotos en desarrollo"],
      cta: "Iniciar una conversación",
    },
    studio: {
      eyebrow: "Una nota del estudio",
      body: "VLR Software es un estudio pequeño e independiente. Construimos las herramientas que queremos que existan — discretas, rápidas y respetuosas con tus datos. Sin trucos de crecimiento. Software que hace su trabajo y se aparta. Si eso suena anticuado, no nos importa.",
      sig: "VLR Software LLC",
    },
    productPage: {
      back: "Todos los productos",
      inHand: "En la mano",
      whatsNew: "Novedades",
      about: "Acerca del producto",
      documents: "Documentación del producto",
      keyFeatures: "Funciones clave",
      version: "Versión",
      updated: "Actualizada",
      pricing: "Precio",
      privacySummary: "Resumen de privacidad",
      privacyTitle: "No hay nada que vulnerar, porque no hay nada que robar.",
      privacyMore: "Todos los detalles en nuestra",
      privacyPolicy: "política de privacidad",
      nextProduct: "Siguiente producto",
    },
    enterprise: {
      eyebrow: "Empresas y alianzas",
      title: "Software, hecho para tu equipo.",
      body: "Para médicos, codificadores y socios que necesitan las herramientas de VLR Software a escala de equipo — o algo construido específicamente para ellos.",
      offerings: [
        { n: "01", title: "Licencias por volumen", body: "Compra cualquier app de VLR Software en cantidad a través de Apple Business Manager y distribuye licencias gestionadas en tu organización." },
        { n: "02", title: "Pilotos en desarrollo", body: "Acceso anticipado y un ciclo de retroalimentación real para las apps en desarrollo, diseñado con los equipos que las usarán." },
        { n: "03", title: "Desarrollo personalizado", body: "Herramientas construidas a propósito con el mismo estándar — offline-first, privadas, mantenidas. Alcance discreto, entrega cuidadosa." },
      ],
      scaleNote: "Offline-first · Sin cuentas · Sin rastreo — a cualquier escala",
      formTitle: "Inicia una conversación",
      formNote: "Respondemos en dos días hábiles. Nada de lo que envíes se guarda en este sitio.",
      labels: { name: "Nombre", organization: "Organización", email: "Email", interest: "Me interesa", message: "Mensaje" },
      placeholders: { name: "Tu nombre", organization: "Clínica, hospital o empresa", email: "tu@organizacion.com", message: "Cuéntanos sobre tu equipo y lo que necesitas." },
      options: ["Licencias por volumen", "Pilotos en desarrollo", "Desarrollo personalizado", "Acceso anticipado a apps en desarrollo", "Otra cosa"],
      submit: "Enviar consulta",
      confirmation: "Tu cliente de correo se abrió con la consulta lista — solo pulsa enviar.",
    },
    press: {
      eyebrow: "Prensa y medios",
      title: "Los hechos, listos para publicar.",
      body: "Todo lo que una redacción necesita para escribir sobre VLR Software — sin embargo, sin adornos. Para entrevistas o copias de reseña, escríbenos directamente.",
      boilerplateLabel: "Texto corporativo",
      boilerplate: "VLR Software LLC es un estudio de software independiente registrado en Estados Unidos. Desde 2011 construye herramientas enfocadas y privadas para la salud, las finanzas y el trabajo profesional — incluida la familia de referencia ICD-10 usada por médicos y codificadores, y Acroly, una biblioteca de abreviaturas médicas para iPhone, iPad y Mac. Las apps de VLR Software funcionan sin conexión, sin cuentas y sin rastreo.",
      productsLabel: "Productos",
      factsLabel: "Datos clave",
      facts: [["Empresa", "VLR Software LLC"], ["Tipo", "Independiente, capital privado"], ["Registrada", "Estados Unidos"], ["En la App Store", "Desde 2010"], ["Enfoque", "Salud, finanzas y trabajo profesional"], ["Principios", "Claridad · Privacidad · Uso continuo"]],
      kitLabel: "Recurso de marca",
      kitText: "Descarga el logotipo aprobado de VLR Software para uso editorial. Para entrevistas, material de reseña o recursos adicionales, contáctanos directamente.",
      kitCta: "Descargar logotipo (SVG)",
      contactCta: "Contacto para entrevistas",
    },
    notFound: {
      eyebrow: "Error 404",
      title: "Esta página tomó un giro equivocado.",
      body: "El enlace que seguiste no existe — pero todo lo que creamos está aquí.",
      cta: "Volver al inicio",
      productsCta: "Explorar productos",
    },
    meta: metaEs,
    legalPage: {
      privacy: "Política de Privacidad",
      terms: "Términos de Uso",
    },
    legal: {
      about: {
        eyebrow: "Empresa",
        title: "Software profesional, construido con propósito.",
        updated: null,
        paragraphs: [
          "VLR Software LLC crea productos móviles enfocados para personas que dependen de información clara, flujos de trabajo privados y herramientas confiables.",
          "Construyendo desde 2010 — Más de una década de trabajo de producto en referencia médica, finanzas personales y utilidades profesionales, con actualizaciones cuidadosas y atención directa al cliente.",
          "Privacidad como arquitectura — Favorecemos el procesamiento en el dispositivo, el almacenamiento bajo control del usuario y límites claros. Los datos sensibles no deben viajar más allá de lo que la tarea requiere.",
          "Un mismo estándar en cada producto — Diferentes apps comparten la misma base: jerarquía útil, comportamiento transparente, interacción accesible y atención a la calidad a largo plazo.",
        ],
        factsTitle: "Información de la empresa",
        facts: [
          ["Nombre legal", "VLR Software LLC"],
          ["Dirección postal", "30 N Gould St Ste R\nSheridan, WY 82801\nUnited States"],
          ["Actividad", "Estudio independiente de software móvil"],
          ["Contacto oficial", "support@vlrsoftware.com"],
        ],
        quote: "El objetivo no es añadir más software a tu día. Es hacer más clara una parte importante de tu día.",
      },
      privacy: {
        eyebrow: "Legal",
        title: "La privacidad es una decisión de producto.",
        updated: "Última actualización: julio de 2026",
        paragraphs: [
          "Esta página explica el enfoque general de la empresa. Los detalles específicos de cada producto se publican en la página de privacidad de cada app.",
          "Nuestro enfoque — VLR Software LLC diseña sus aplicaciones para evitar cuentas, rastreo y recolección en servidores innecesarios. Buscamos recolectar solo lo que una función genuinamente necesita.",
          "Detalles por producto — Cada app puede usar diferentes servicios de plataforma como iCloud, StoreKit, notificaciones o inteligencia en el dispositivo. Cada producto publica su propia política de privacidad para que esas diferencias sean explícitas.",
          "Servicios de plataforma — Cuando una función usa servicios de Apple, Google u otro proveedor de plataforma, ese servicio también se rige por la política de privacidad y los términos del proveedor.",
          "Preguntas — Si tienes una pregunta de privacidad sobre el sitio o cualquier producto de VLR Software LLC, escribe a support@vlrsoftware.com.",
        ],
      },
      terms: {
        eyebrow: "Legal",
        title: "Términos y Condiciones",
        updated: null,
        markdown: true,
        paragraphs: [],
      },
    },
    footer: {
      products: "Productos",
      company: "Empresa",
      legal: "Legal",
      contact: "Contacto",
      emailUs: "Escríbenos",
      comingSoon: "Próximamente",
      about: "Acerca de",
      press: "Prensa",
      enterprise: "Empresas",
      privacy: "Privacidad",
      terms: "Términos",
      language: "Idioma",
      rights: "© 2026 VLR Software LLC. Todos los derechos reservados.",
      tagline: "Software que prioriza la privacidad para la salud, las finanzas y el trabajo profesional.",
    },
  },
};
